import { OrphanSegment, useOrphans, useRestoreOrphan, useDeleteOrphan } from '@/hooks/useOrphans'

interface Props {
  slug: string
  open: boolean
  onClose: () => void
}

export function OrphansPanel({ slug, open, onClose }: Props) {
  const { data } = useOrphans(slug)
  const restore = useRestoreOrphan(slug)
  const del = useDeleteOrphan(slug)

  if (!open) return null
  const chapters = (data?.chapters ?? []).filter((c) => c.orphanedSegments.length > 0)

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Orphaned paragraphs ({data?.orphanedCount ?? 0})
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800">
            ✕
          </button>
        </div>
        {chapters.length === 0 && (
          <p className="text-sm text-slate-500">No orphans. 🎉</p>
        )}
        {chapters.map((ch) => (
          <section key={ch.id} className="mb-4">
            <h3 className="mb-2 text-sm font-medium text-slate-700">{ch.displayTitle}</h3>
            <ul className="space-y-2">
              {ch.orphanedSegments.map((s: OrphanSegment) => (
                <li key={s.id} className="rounded-lg border border-slate-200 p-3">
                  <div className="mb-1 text-xs text-slate-400">{s.externalId}</div>
                  <p className="text-sm text-slate-800">{s.sourceText}</p>
                  {s.targetText && (
                    <p className="mt-1 text-sm italic text-slate-500">{s.targetText}</p>
                  )}
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => restore.mutate(s.id)}
                      className="rounded-md border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50"
                    >
                      Restore
                    </button>
                    <button
                      onClick={() => del.mutate(s.id)}
                      className="rounded-md bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  )
}
